import React, { useEffect, useState } from "react";
import { useCurrentTool } from "@/shared/hooks/useCurrentTool"
import { Button } from "@/shared/ui/button"
import InlineAudioPlayer from '@/shared/components/InlineAudioPlayer';
import { useGetPlaybackUrlQuery } from '@/api/rtk/reportsApi'

const MAX_RETRIES = 3;

function AudioPlayer({ reportId, audioDuration }) {
  const { nonAudioTool } = useCurrentTool();
  const skip = !reportId || nonAudioTool;
  const { data: playbackUrl, isFetching, isError, isSuccess, refetch } = useGetPlaybackUrlQuery(reportId, {
    skip,
  });
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const [retryCount, setRetryCount] = useState(0);
  const [loadFailed, setLoadFailed] = useState(false);

  const handleSpeedChange = () => {
    let newSpeed;
    if (playbackSpeed === 1) {
      newSpeed = 1.25;
    } else if (playbackSpeed === 1.25) {
      newSpeed = 1.5;
    } else if (playbackSpeed === 1.5) {
      newSpeed = 2;
    } else {
      newSpeed = 1;
    }
    setPlaybackSpeed(newSpeed);
  };

  useEffect(() => {
    setRetryCount(0);
    setLoadFailed(false);
  }, [reportId]);

  useEffect(() => {
    if (isSuccess && !playbackUrl) setLoadFailed(true);
  }, [isSuccess, playbackUrl]);

  useEffect(() => {
    if (skip || loadFailed || !isError) return undefined;
    if (retryCount >= MAX_RETRIES) {
      setLoadFailed(true);
      return undefined;
    }
    const timer = setTimeout(() => {
      setRetryCount((count) => count + 1);
      refetch();
    }, 1000 * (retryCount + 1));
    return () => clearTimeout(timer);
  }, [isError, skip, loadFailed, retryCount, refetch]);

  const handleAudioError = () => {
    if (isFetching || loadFailed) return;
    if (retryCount < MAX_RETRIES) {
      setRetryCount((count) => count + 1);
      refetch();
      return;
    }
    setLoadFailed(true);
  };

  if (nonAudioTool) return null;

  return (
    <div className="flex items-center gap-3 border-y w-full min-w-0 py-5 sm:py-6">
      <div className="flex-1 min-w-0 flex items-center justify-start">
        {loadFailed ? (
          <span className="text-sm text-zinc-400">Failed to load audio</span>
        ) : (
          <InlineAudioPlayer
            src={playbackUrl}
            variant="report"
            urlLoading={!playbackUrl && !loadFailed}
            playbackRate={playbackSpeed}
            onMediaError={handleAudioError}
            knownDurationSeconds={audioDuration}
          />
        )}
      </div>
      <Button
        onClick={handleSpeedChange}
        disabled={!playbackUrl || loadFailed}
        variant="outline"
        size="icon"
        className="shrink-0 duration-200 text-[#71717a] hover:text-[#71717a] font-semibold w-11 shadow-md transform hover:scale-100 active:scale-95"
        title="Change playback speed"
      >
        {playbackSpeed}x
      </Button>
    </div>
  );
}

export default AudioPlayer;
